import { Divider } from "@mui/material";
import { HubCard } from "~/components/hub-card";
import {
  AggregateScoreOrder,
  AggregateScoreOrderField,
  AggregateScoreState,
  AggregateScoreType,
  OrderDirection,
  useGetActivePoliciesCountQuery,
  useGetAggregateScoresQuery,
} from "~/operations";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { FirewatchList } from "~/components/FirewatchList";
import { SpaceOrWorkspaceScope, SpaceScope } from "~/hooks/useScope";
import { useDailyPriorityFindingCounts } from "~/components/PriorityFindings/useDailyPriorityFindingCounts";
import { useDailyAssetCounts } from "~/components/PriorityFindings/useDailyAssetCounts";

import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { PriorityFindingsIcon } from "~/icons";
import { useLoadScope } from "~/pages/space/home-dashboard/hooks/useLoadScope";
import { PriorityFindingsSkeleton } from "~/pages/space/home-dashboard/PriorityFindingsCard/components/PriorityFindingsCardSkeleton";
import { PriorityFindingsCardEmptyStateFindings } from "~/pages/space/home-dashboard/PriorityFindingsCard/components/PriorityFindingsCardEmptyState";
import { PriorityFindingsCardStats } from "~/pages/space/home-dashboard/PriorityFindingsCard/components/PriorityFindingsCardStat";

interface PriorityFindingsDashboardCardProps {
  scope: SpaceOrWorkspaceScope;
  spaceScope: SpaceScope;
  assetsTotalCount: number;
}

export function PriorityFindingsCard({
  scope,
  spaceScope,
  assetsTotalCount,
}: PriorityFindingsDashboardCardProps) {
  const { loading, findingsCount, assetCount } = useLoadScope();
  const { isFeatureEnabled } = useFeatureFlags();
  const sort = useSort<AggregateScoreOrder>({
    defaultSort: {
      field: AggregateScoreOrderField.Rank,
      direction: OrderDirection.Asc,
    },
  });

  const { data: activePoliciesData, loading: loadingActivePolicies } =
    useGetActivePoliciesCountQuery({
      variables: {
        input: {
          scopeMrn: scope?.mrn || "",
        },
      },
      skip: !scope?.mrn,
    });

  const {
    countDelta: findingsDelta,
    countDeltaPercentage: findingsDeltaPercentage,
    isLoading: findingsMetricsLoading,
  } = useDailyPriorityFindingCounts({
    entityMrn: scope.mrn,
  });

  const {
    countDelta: assetCountDelta,
    countDeltaPercentage: assetDeltaPercentage,
    isLoading: assetMetricsLoading,
  } = useDailyAssetCounts({ entityMrn: scope.mrn });

  const isMoveTheNeedleFeatureEnabled = isFeatureEnabled("Move the needle");

  const { data, loading: aggScoresLoading } = useGetAggregateScoresQuery({
    variables: {
      entityMrn: scope.mrn,
      first: isMoveTheNeedleFeatureEnabled ? 5 : 8,
      after: null,
      filter: {
        minRiskValue: 1,
        scoreTypes: [
          AggregateScoreType.Check,
          AggregateScoreType.Vulnerability,
        ],
        state: AggregateScoreState.Enabled,
      },
      orderBy: sort.orderBy,
    },
    skip: !scope,
  });

  const scoresUnion = data?.aggregateScores;
  const scores =
    scoresUnion?.__typename === "AggregateScoresConnection"
      ? scoresUnion
      : undefined;
  const findings = scores?.edges || [];
  const hasFindings = Boolean(findings.length);
  const activePoliciesCount = activePoliciesData?.activePolicies?.totalCount;

  const isLoading =
    loading ||
    aggScoresLoading ||
    assetMetricsLoading ||
    findingsMetricsLoading ||
    loadingActivePolicies;

  const totalFindings =
    scoresUnion?.__typename === "AggregateScoresConnection"
      ? scoresUnion.totalCount
      : 0;

  return (
    <HubCard
      loading={false} //We do not want to use the baked in loading feature here
      title="Priority Findings"
      tagline="critical & high risk findings"
      icon={<PriorityFindingsIcon />}
      data-testid="priority-findings-card"
      sx={{
        minHeight: isMoveTheNeedleFeatureEnabled ? 327 : 524,
      }}
    >
      {!isMoveTheNeedleFeatureEnabled &&
        !isLoading &&
        hasFindings &&
        assetsTotalCount && (
          <>
            <PriorityFindingsCardStats
              findingsCount={findingsCount}
              findingsDelta={findingsDelta}
              findingsDeltaPercentage={findingsDeltaPercentage}
              assetCount={assetCount}
              assetCountDelta={assetCountDelta}
              assetDeltaPercentage={assetDeltaPercentage}
            />
            <Divider sx={{ mt: 1, mb: 1 }} />
          </>
        )}

      {isMoveTheNeedleFeatureEnabled && <Divider sx={{ mt: 1, mb: 1 }} />}

      {isLoading && <PriorityFindingsSkeleton />}

      {!isLoading && !hasFindings && (
        <PriorityFindingsCardEmptyStateFindings
          assetsTotalCount={assetsTotalCount}
          activePoliciesCount={activePoliciesCount}
          totalFindings={totalFindings}
        />
      )}

      {!isLoading && hasFindings && assetsTotalCount && (
        <FirewatchList
          size="small"
          space={spaceScope}
          scope={scope}
          pageType="TopSecurityFindings"
          data={findings}
          loading={loading}
          sort={sort}
          disableSorting={true}
        />
      )}
    </HubCard>
  );
}
